import React, { useEffect, useState } from "react";
import { Wallet, Tag, TrendingUp, Boxes, RefreshCw, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { api, formatApiError, downloadFile } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";

const fmtIDR = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");

export default function AssetReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dl, setDl] = useState(false);

  const load = () => {
    setLoading(true); setErr("");
    api.get("/opname/asset-report")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Gagal memuat laporan."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const download = () => {
    setDl(true);
    downloadFile("/opname/asset-report/export", {}, "laporan-aset-stok.xlsx")
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Gagal mengunduh Excel."))
      .finally(() => setDl(false));
  };

  const g = data?.grand || {};
  const cards = [
    { label: "Total Modal (HPP)", val: g.hpp_total, icon: Wallet, cls: "text-amber-700" },
    { label: "Total Harga Jual", val: g.sell_total, icon: Tag, cls: "text-emerald-700" },
    { label: "Potensi Margin", val: g.potential_margin, icon: TrendingUp, cls: "text-blue-700" },
  ];

  return (
    <div data-testid="asset-report-page">
      <PageHeader title="Laporan Aset Stok" subtitle="Nilai stok per gudang dihitung dari modal (HPP) dan harga jual — untuk tahu total aset">
        <Button onClick={download} variant="outline" className="border-status-passed text-status-passed gap-2" data-testid="asset-export-btn" disabled={dl || loading || !data?.warehouses?.length}>
          <FileSpreadsheet className={`w-4 h-4 ${dl ? "animate-pulse" : ""}`} /> Unduh Excel
        </Button>
        <Button onClick={load} variant="outline" className="border-taupe text-cocoa gap-2" data-testid="asset-refresh" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Muat Ulang
        </Button>
      </PageHeader>

      {err && <Panel className="p-4 mb-4 text-sm text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{err}</Panel>}
      {loading && !data && <Panel className="p-8 text-center text-wmuted">Menghitung nilai aset...</Panel>}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-4">
            {cards.map((c) => { const Icon = c.icon; return (
              <div key={c.label} className="rounded-xl border border-taupe/20 bg-ivory/40 p-4" data-testid={`asset-grand-${c.label}`}>
                <div className="flex items-center gap-2 text-xs text-wmuted"><Icon className="w-4 h-4" />{c.label}</div>
                <div className={`mt-1 text-2xl font-semibold ${c.cls}`}>{fmtIDR(c.val)}</div>
              </div>
            ); })}
          </div>
          <div className="text-xs text-wmuted mb-3" data-testid="asset-units">
            Total {Number(g.units || 0).toLocaleString("id-ID")} pcs dari {data.warehouses.length} gudang.
            {data.units_without_price > 0 && <span className="text-amber-700"> · {data.units_without_price.toLocaleString("id-ID")} pcs belum punya harga (modal/jual = 0), tidak ikut dihitung nilainya.</span>}
          </div>

          <Panel className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cocoa/5 text-wmuted">
                <tr>
                  <th className="p-3 text-left font-medium">Gudang</th>
                  <th className="p-3 text-right font-medium">Jumlah (pcs)</th>
                  <th className="p-3 text-right font-medium">Total Modal (HPP)</th>
                  <th className="p-3 text-right font-medium">Total Harga Jual</th>
                  <th className="p-3 text-right font-medium">Potensi Margin</th>
                </tr>
              </thead>
              <tbody>
                {data.warehouses.map((w) => (
                  <tr key={w.warehouse_id} className="border-t border-taupe/15" data-testid={`asset-wh-${w.warehouse_id}`}>
                    <td className="p-3 text-cocoa font-medium">{w.warehouse_name}</td>
                    <td className="p-3 text-right">{Number(w.units).toLocaleString("id-ID")}</td>
                    <td className="p-3 text-right text-amber-700">{fmtIDR(w.hpp_total)}</td>
                    <td className="p-3 text-right text-emerald-700">{fmtIDR(w.sell_total)}</td>
                    <td className="p-3 text-right text-blue-700">{fmtIDR(w.potential_margin)}</td>
                  </tr>
                ))}
                {!data.warehouses.length && <tr><td colSpan={5} className="p-6 text-center text-wmuted">Belum ada stok.</td></tr>}
              </tbody>
              {data.warehouses.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-cocoa/20 bg-ivory/40 font-semibold text-cocoa" data-testid="asset-total-row">
                    <td className="p-3">TOTAL ASET</td>
                    <td className="p-3 text-right">{Number(g.units).toLocaleString("id-ID")}</td>
                    <td className="p-3 text-right text-amber-700">{fmtIDR(g.hpp_total)}</td>
                    <td className="p-3 text-right text-emerald-700">{fmtIDR(g.sell_total)}</td>
                    <td className="p-3 text-right text-blue-700">{fmtIDR(g.potential_margin)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </Panel>
        </>
      )}
    </div>
  );
}
